import type {
  CredentialCreationOptionsJSON,
  PublicKeyCredentialWithAttestationJSON,
} from "@github/webauthn-json"
import JSCookie from "js-cookie"

interface Action {
  href: string
}

// TODO: unhardcode cookie name. This is coordinated with frontend
const COOKIE_NAME = "hanko"
const HTTP_REGEX = /^https:\/\//

interface HankoState<
  N extends string,
  T extends Record<string, Action> | undefined = undefined,
  K extends Record<string, any> | undefined = undefined,
> {
  name: N
  csrf_token: string
  actions: T
  payload: K
  error?: { code: string; message: string }
}

type PreflightState = HankoState<
  "preflight",
  { register_client_capabilities: Action }
>
type LoginInitActions = HankoState<
  "login_init",
  { continue_with_login_identifier: Action }
>
type RegistrationInitState = HankoState<
  "registration_init",
  { register_login_identifier: Action }
>
export type PasscodeConfirmationState = HankoState<
  "passcode_confirmation",
  {
    verify_passcode: Action
    resend_passcode: Action
  },
  { resend_after: number } | undefined
>
export type OnboardingCreatePasskeyState = HankoState<
  "onboarding_create_passkey",
  {
    skip: Action
    webauthn_generate_creation_options: Action
  },
  { user: { user_id: string } } | undefined
>
type OnboardingVerifyPasskeyAttestationState = HankoState<
  "onboarding_verify_passkey_attestation",
  {
    back: Action
    webauthn_verify_attestation_response: Action
  },
  { creation_options: CredentialCreationOptionsJSON }
>
type SuccessState = HankoState<"success">

export type States =
  | PreflightState
  | RegistrationInitState
  | LoginInitActions
  | PasscodeConfirmationState
  | OnboardingCreatePasskeyState
  | OnboardingVerifyPasskeyAttestationState
  | SuccessState

interface SessionDetail {
  is_valid: boolean
  expiration_time?: string
  user_id?: string
}

export function createHankoFlow(config: {
  apiUrl: string
  cookieDomain: string
}) {
  function tenantId() {
    const match = config.apiUrl.match(/https:\/\/(.*).hanko.io.*/)
    if (!match?.[1]) {
      throw new Error("Invalid Hanko API URL")
    }
    return match[1]
  }

  async function apiCall<T>(
    body: Record<string, any> | null,
    action: string,
    method?: "POST" | "GET",
    headers?: Record<string, string>,
  ) {
    const response = await fetch(`https://${tenantId()}.hanko.io${action}`, {
      method: method ?? "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: body ? JSON.stringify(body) : undefined,
    })

    const jwt = response.headers.get("x-auth-token")
    const expirationSeconds = Number.parseInt(
      response.headers.get("x-session-lifetime") || "0",
      10,
    )
    const retention = response.headers.get("x-session-retention")

    if (jwt) {
      const secure =
        !!config.apiUrl.match(HTTP_REGEX) &&
        !!window.location.href.match(HTTP_REGEX)

      JSCookie.set(COOKIE_NAME, jwt, {
        secure,
        expires:
          retention === "session"
            ? undefined
            : new Date(Date.now() + expirationSeconds * 1000),
        domain: config.cookieDomain,
        sameSite: secure ? "none" : "lax",
      })
    }

    return (await response.json()) as T
  }

  async function validateSession() {
    const jwt = JSCookie.get(COOKIE_NAME)
    if (!jwt) {
      return { is_valid: false } as SessionDetail
    }

    return await apiCall<SessionDetail>(null, "/sessions/validate", "GET", {
      Authorization: `Bearer ${jwt}`,
    })
  }

  async function preflightLogin(email: string) {
    const preflightState = await apiCall<PreflightState>(null, "/login")
    if (preflightState.error) {
      return preflightState
    }

    const registerClientCapabilitiesState =
      await registerClientCapabilities<LoginInitActions>(preflightState)

    return await apiCall<PasscodeConfirmationState>(
      {
        input_data: { email },
        csrf_token: registerClientCapabilitiesState.csrf_token,
      },
      registerClientCapabilitiesState.actions.continue_with_login_identifier
        .href,
    )
  }

  async function preflightRegistration(email: string) {
    const preflightState = await apiCall<PreflightState>(null, "/registration")
    if (preflightState.error) return preflightState

    const registerClientCapabilitiesState =
      await registerClientCapabilities<RegistrationInitState>(preflightState)
    if (registerClientCapabilitiesState.error)
      return registerClientCapabilitiesState

    return await apiCall<PasscodeConfirmationState>(
      {
        input_data: { email },
        csrf_token: registerClientCapabilitiesState.csrf_token,
      },
      registerClientCapabilitiesState.actions.register_login_identifier.href,
    )
  }

  async function registerClientCapabilities<
    T extends RegistrationInitState | LoginInitActions,
  >(preflightState: PreflightState) {
    const webauthnAvailable = !!window.PublicKeyCredential
    const conditionalMediationAvailable = webauthnAvailable
      ? await (PublicKeyCredential.isConditionalMediationAvailable?.() || false)
      : false
    const platformAuthenticatorAvailable = webauthnAvailable
      ? await (PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.() ||
          false)
      : false

    return await apiCall<T>(
      {
        input_data: {
          webauthn_available: webauthnAvailable,
          webauthn_conditional_mediation_available:
            conditionalMediationAvailable,
          webauthn_platform_authenticator_available:
            platformAuthenticatorAvailable,
        },
        csrf_token: preflightState.csrf_token,
      },
      preflightState.actions.register_client_capabilities.href,
    )
  }

  async function resendPasscode({
    state,
  }: {
    state: PasscodeConfirmationState
  }) {
    return await apiCall<PasscodeConfirmationState>(
      {
        input_data: {},
        csrf_token: state.csrf_token,
      },
      state.actions.resend_passcode.href,
    )
  }

  async function onboardingCreatePasskey({
    code,
    state,
  }: {
    code: string
    state: PasscodeConfirmationState
  }) {
    return await apiCall<OnboardingCreatePasskeyState>(
      {
        input_data: { code },
        csrf_token: state.csrf_token,
      },
      state.actions.verify_passcode.href,
    )
  }

  async function webauthnGenerateCreationOptions({
    state,
  }: {
    state: OnboardingCreatePasskeyState
  }) {
    return await apiCall<OnboardingVerifyPasskeyAttestationState>(
      {
        input_data: {},
        csrf_token: state.csrf_token,
      },
      state.actions.webauthn_generate_creation_options.href,
    )
  }

  async function webauthnVerifyAttestationResponse({
    state,
    publicKey,
  }: {
    state: OnboardingVerifyPasskeyAttestationState
    publicKey: PublicKeyCredentialWithAttestationJSON
  }) {
    return await apiCall<any>(
      {
        input_data: { public_key: publicKey },
        csrf_token: state.csrf_token,
      },
      state.actions.webauthn_verify_attestation_response.href,
    )
  }

  async function back<
    A extends HankoState<any, any, any>,
    B extends HankoState<any, any, any>,
  >({ state }: { state: A }) {
    return await apiCall<B>(
      {
        input_data: {},
        csrf_token: state.csrf_token,
      },
      state.actions.back.href,
    )
  }

  async function skip<
    A extends HankoState<any, any, any>,
    B extends HankoState<any, any, any>,
  >({ state }: { state: A }) {
    return await apiCall<B>(
      {
        input_data: {},
        csrf_token: state.csrf_token,
      },
      state.actions.skip.href,
    )
  }

  return {
    preflightRegistration,
    preflightLogin,
    resendPasscode,
    onboardingCreatePasskey,
    skip,
    back,
    validateSession,
    webauthnGenerateCreationOptions,
    webauthnVerifyAttestationResponse,
  }
}
